/**
 * The country/dial-code list for every phone input in the auth flow.
 *
 * Built once at module load. It is ~240 entries and it never changes, so
 * rebuilding it inside a component would run on every keystroke of whatever
 * form it sits in.
 *
 * Dial codes come from the phone library rather than the API: `phoneCode` is
 * null for many rows in /api/geography/countries/.
 */

import { getCountries } from "libphonenumber-js";

import { DEFAULT_COUNTRY, getDialCode } from "./authMappers";

const regionNames =
  typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

export const COUNTRY_OPTIONS = getCountries()
  .map((iso2) => {
    const dial = getDialCode(iso2);
    if (!dial) return null;

    const name = regionNames?.of(iso2) || iso2;

    return { value: iso2, label: `${iso2} +${dial}`, title: `${name} +${dial}` };
  })
  .filter(Boolean)
  .sort((a, b) => a.value.localeCompare(b.value));

const SUPPORTED = new Set(COUNTRY_OPTIONS.map((option) => option.value));

/** The browser's region, falling back to GH — the app uses the device region. */
export const detectCountry = () => {
  try {
    const region = (navigator.language || "").split("-")[1]?.toUpperCase();
    if (region && SUPPORTED.has(region)) return region;
  } catch {
    /* fall through */
  }

  return DEFAULT_COUNTRY;
};

/** Guards a stored country before it seeds a form. */
export const safeCountry = (iso2) => {
  const code = String(iso2 || "").toUpperCase();

  return SUPPORTED.has(code) ? code : detectCountry();
};
